import { useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { UploadFile as UploadFileIcon } from "@mui/icons-material";
import {
  Button,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { ulid } from "ulid";

import { CenteredLoading, useSnackbar } from "@/features/ui";

import { ImportStockAdjustmentDialog } from "../import-stock-adjustment-dialog";
import { AddStockAdjustmentProductVariantSection } from "./add-stock-adjustment-product-variant-section";
import { buildStockAdjustmentPayload } from "./build-stock-adjustment-payload";
import { GeneralInformationSection } from "./general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./stock-adjustment-form-constants";
import { StockAdjustmentProductVariantsSection } from "./stock-adjustment-product-variants-section";

export const StockAdjustmentForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
  onImported,
}) => {
  const { showSnackbar } = useSnackbar();
  const [importHeader, setImportHeader] = useState(null);

  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { control, handleSubmit, formState, getValues, trigger } = methods;

  const { append, fields } = useFieldArray({
    control,
    name: "stockAdjustmentProductVariants",
  });

  const warehouse = useWatch({ control, name: "warehouse" });
  const outlet = useWatch({ control, name: "outlet" });

  const onFormSubmit = async (formData) => {
    await onSubmit(buildStockAdjustmentPayload(formData, { editMode }));
  };

  // The import dialog takes the header from here, so a bad header is caught before a file is read.
  const handleOpenImport = async () => {
    const headerIsValid = await trigger([
      "outlet",
      "warehouse",
      "transactionDate",
      "adjustmentChartOfAccount",
    ]);
    if (!headerIsValid) {
      showSnackbar(
        "Lengkapi outlet, gudang, tanggal, dan akun penyesuaian pada Informasi Umum sebelum mengimpor",
        "warning"
      );
      return;
    }
    setImportHeader(getValues());
  };

  const handleLineAdded = (productVariant, product, line) => {
    const existingIndex = fields.findIndex((f) => f.sku === productVariant.sku);
    if (existingIndex !== -1) return;

    append({
      id: ulid(),
      productVariantId: productVariant.id,
      name: productVariant.name,
      sku: productVariant.sku,
      unitOfMeasurement: product.unitOfMeasurement.name,
      type: line.type,
      quantity: line.quantity,
      costPerUnit: line.costPerUnit,
      note: "",
    });
  };

  if (isLoading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="h6">Tambah Produk</Typography>
                {onImported && !editMode && (
                  <Tooltip
                    title={
                      fields.length > 0
                        ? "Hapus baris yang sudah ditambahkan secara manual untuk memakai impor"
                        : "Buat penyesuaian dari file Excel atau CSV"
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        disabled={fields.length > 0}
                        onClick={handleOpenImport}
                      >
                        Import dari Excel/CSV
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
              <AddStockAdjustmentProductVariantSection
                onAdded={handleLineAdded}
                warehouse={warehouse}
                outletId={outlet?.id}
              />
              <Divider />
              <Typography variant="h6">Penyesuaian Stok</Typography>
              <StockAdjustmentProductVariantsSection editMode={editMode} />
            </Stack>
          </Paper>
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>

      {onImported && !editMode && (
        <ImportStockAdjustmentDialog
          open={Boolean(importHeader)}
          onClose={() => setImportHeader(null)}
          headerValues={importHeader}
          onImported={(result) => {
            setImportHeader(null);
            onImported(result);
          }}
        />
      )}
    </FormProvider>
  );
};

StockAdjustmentForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
  // Create only: called with `{ id, referenceNumber }` once a file import has created the document.
  onImported: PropTypes.func,
};
